import React, { JSX } from "react";
import IRMA from "@models/IRMA";
import IRMA_Line_Item from "@models/IRMA_Line_Item";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import OwTable from "@components/shared/OwTable/OwTable";
import IOwTableCols from "@models/IOwTableCols";
import "./RmaLineItemsSection.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import RmaStatusEnum from "@models/RmaStatusEnum";

type Props = {
    rma: IRMA;
    onAddInspection?: (lineItem: IRMA_Line_Item) => void;
};

export const RmaLineItemsSection = (props: Props) => {
    const { rma, onAddInspection } = props;
    const { userModel, supplierModel } = useAppContextNonNull();

    const getProgressBar = (received: number, returned: number): JSX.Element => {
        const percentage = returned > 0 ? Math.round((received / returned) * 100) : 0;

        return (
            <div className="d-flex align-items-center">
                <span className="me-2">
                    {received}/{returned}
                </span>
                <div className="progress flex-grow-1" style={{ height: "20px" }}>
                    <div
                        className={`progress-bar ${percentage === 100 ? "bg-success" : "bg-info"}`}
                        role="progressbar"
                        style={{ width: `${percentage}%` }}
                        aria-valuenow={percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}>
                        {percentage}%
                    </div>
                </div>
            </div>
        );
    };

    const getInspectionCount = (lineItem: IRMA_Line_Item): string => {
        const inspections = lineItem.RMA_Line_Item_Inspections || [];
        const totalInspected = inspections.reduce((sum, insp) => sum + insp.quantity, 0);
        return `${totalInspected} inspected`;
    };

    const columns: IOwTableCols[] = [
        {
            title: "sku",
            valueProp: "sku",
            className: "w-25",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => <span>{row.sku}</span>,
        },
        {
            title: "product",
            valueProp: "catalog_item_name",
            className: "w-25",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => <span>{row.catalog_item_name}</span>,
        },
        {
            title: "qty_ordered",
            valueProp: "qty_ordered",
            className: "w-10",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => <span>{row.qty_ordered}</span>,
        },
        {
            title: "qty_returned",
            valueProp: "qty_returned",
            className: "w-10",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => <span>{row.qty_returned}</span>,
        },
        {
            title: "qty_received",
            valueProp: "qty_received",
            className: "w-20",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => getProgressBar(row.qty_received || 0, row.qty_returned),
        },
        {
            title: "return_reason",
            valueProp: "Return_Reason.name",
            className: "w-15",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => <span>{row.Return_Reason ? <OwLabel title={row.Return_Reason?.name} /> : "-"}</span>,
        },
        {
            title: "inspections",
            valueProp: "inspections",
            className: "w-10",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => <span className="text-muted">{getInspectionCount(row)}</span>,
        },
        {
            title: "actions",
            valueProp: "actions",
            className: "w-10",
            renderFnEmitsRow: true,
            renderFn: (row: IRMA_Line_Item) => {
                const canAddInspection = rma.rma_status_id === RmaStatusEnum.RECEIVED;

                return (
                    <div>
                        {canAddInspection && onAddInspection && (
                            <OwButton title="inspect" fn={() => onAddInspection(row)} className="btn-sm btn-info" />
                        )}
                    </div>
                );
            },
        },
    ];

    const lineItems = rma.RMA_Line_Items || [];

    return (
        <div className="RmaLineItemsSection mt-4">
            <h4 className="mb-3">
                <OwLabel title="rma_line_items" isBold={true} />
            </h4>
            {lineItems.length > 0 ? (
                <OwTable
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataSet={lineItems || []}
                    columns={columns}
                    dataFetch={false}
                    initialData={false}
                />
            ) : (
                <div className="text-muted">
                    <OwLabel title="no_line_items" />
                </div>
            )}
        </div>
    );
};
